
import {Layer} from './Layer.js';
import * as Util from '../core/Util.js';
/** @import {InteractiveLayerOptions} from './Layer.js' */
/** @import {MaplairMap} from '../map/Map.js' */

/**
 * Used to group several layers and handle them as one. If you add it to the map,
 * any layers added or removed from the group will be added/removed on the map as
 * well. Extends `Layer`.
 *
 * @example
 * ```js
 * new LayerGroup([marker1, marker2])
 * 	.addLayer(polyline)
 * 	.addTo(map);
 * ```
 */
export class LayerGroup extends Layer {

	/**
	 * Creates a layer group, optionally given an initial set of layers and an
	 * `options` object.
	 * @param {Layer[]} [layers]
	 * @param {InteractiveLayerOptions} [options]
	 */
	constructor(layers, options) { // for compatibility of code using `LayerGroup.extend`
		super();
		Util.setOptions(this, options);

		this._layers = {};

		for (const layer of layers ?? []) {
			this.addLayer(layer);
		}

		this.callInitHooks(LayerGroup);
	}

	/**
	 * Adds the given layer to the group.
	 * @param {Layer} layer
	 * @returns {this}
	 */
	addLayer(layer) {
		const id = this.getLayerId(layer);

		this._layers[id] = layer;

		this._map?.addLayer(layer);

		return this;
	}

	/**
	 * Removes the given layer, or the layer with the given internal ID, from the group.
	 * @param {Layer|number} layer
	 * @returns {this}
	 */
	removeLayer(layer) {
		const id = layer in this._layers ? layer : this.getLayerId(layer);

		if (this._map && this._layers[id]) {
			this._map.removeLayer(this._layers[id]);
		}

		delete this._layers[id];

		return this;
	}

	/**
	 * Returns `true` if the given layer, or the layer with the given internal ID, is
	 * currently added to the group.
	 * @param {Layer|number} layer
	 * @returns {boolean}
	 */
	hasLayer(layer) {
		const layerId = typeof layer === 'number' ? layer : this.getLayerId(layer);
		return layerId in this._layers;
	}

	/**
	 * Removes all the layers from the group.
	 * @returns {this}
	 */
	clearLayers() {
		return this.eachLayer(this.removeLayer, this);
	}

	/**
	 * Adds the layers of the group to the map.
	 * @param {MaplairMap} map
	 */
	onAdd(map) {
		this.eachLayer(map.addLayer, map);
	}

	/**
	 * Removes the layers of the group from the map.
	 * @param {MaplairMap} map
	 */
	onRemove(map) {
		this.eachLayer(map.removeLayer, map);
	}

	/**
	 * Iterates over the layers of the group, optionally specifying context of the
	 * iterator function.
	 * ```js
	 * group.eachLayer(layer => layer.bindPopup('Hello'));
	 * ```
	 * @param {(layer: Layer) => void} method
	 * @param {object} [context]
	 * @returns {this}
	 */
	eachLayer(method, context) {
		for (const layer of Object.values(this._layers)) {
			method.call(context, layer);
		}
		return this;
	}

	/**
	 * Returns the layer with the given internal ID.
	 * @param {number} id
	 * @returns {Layer|undefined}
	 */
	getLayer(id) {
		return this._layers[id];
	}

	/**
	 * Returns an array of all the layers added to the group.
	 * @returns {Layer[]}
	 */
	getLayers() {
		const layers = [];
		this.eachLayer(layers.push, layers);
		return layers;
	}

	/**
	 * Calls `setZIndex` on every layer contained in this group, passing the z-index.
	 * @param {number} zIndex
	 * @returns {this}
	 */
	setZIndex(zIndex) {
		return this.eachLayer(l => l.setZIndex?.(zIndex));
	}

	/**
	 * Returns the internal ID for a layer.
	 * @param {Layer} layer
	 * @returns {number}
	 */
	getLayerId(layer) {
		return Util.stamp(layer);
	}
}
